import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import io from 'socket.io-client';
import { DEVICE_TYPES, GRAPH_TYPES, SOCKET_EVENTS } from '../constants/protocol';
import { buildSpecsFromHeaters } from '../helpers/buildSpecsFromHeaters';
import {
  handleAddHeatingSchedule,
  handleClearHeatingSchedule,
  handleEssGraph,
  handleEssSSRStateSocket,
  handleEssSwitchSocket,
} from '../components/store/slices/essSwitchSlice';
import { handleReceivedThermocoupleSetting } from '../components/store/slices/FaultsSlice';
import { selectLocations } from '../components/store/slices/locationsSlice';
import { selectDescription } from '../components/store/slices/ssrDescriptionSlice';
import {
  handleTesSwitchSocket,
  handleTesSSRStateSocket,
  tesHandleClearHeatingSchedule,
  tesHandleAddHeatingSchedule,
  handleTesGraph,
} from '../components/store/slices/tesSwitchSlice';
import {
  handleTgsGraph,
  handleTgsSwitchSocket,
  tgsHandleAddHeatingSchedule,
  tgsHandleClearHeatingSchedule,
} from '../components/store/slices/tgsSwitchSlice';
import {
  getGraphTypeKey,
  readableTime,
} from '../helpers/helpers';
import { logger } from '../helpers/logger';
import { resyncSystem } from '../helpers/resyncDevices';
import { socketUrl } from '../config';

const SYSTEMS = ['ess', 'tgs', 'tes'];

export const useSocket = (room, accessToken) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(true);
  const dispatch = useDispatch();
  const { elementsOptions } = useSelector(selectDescription);
  const locations = useSelector(selectLocations);

  // Handlers are registered once per connection; read elementsOptions through a ref.
  const elementsOptionsRef = useRef(elementsOptions);
  elementsOptionsRef.current = elementsOptions;

  useEffect(() => {
    if (accessToken && room) {
      const newSocket = io(socketUrl(), {
        query: { room }
      });

      // Pushes sent while the socket was down (or before its first successful
      // attempt) are lost; once it is back in its room, refresh every device.
      let missedUpdates = false;
      newSocket.on('connect', () => {
        logger.info('WebSocket connection established.');
        setConnected(true);
        if (missedUpdates) {
          SYSTEMS.forEach((system) =>
            resyncSystem(dispatch, system).catch((error) =>
              logger.warn(`could not refresh ${system} devices after reconnecting`, error),
            ),
          );
        }
        missedUpdates = false;
      });

      newSocket.on('connect_error', (e) => {
        logger.warn('WebSocket connection error.', e);
        setConnected(false);
        missedUpdates = true;
      });

      newSocket.on(SOCKET_EVENTS.SWITCH_AUDIT, (data) => {
        dispatch(handleEssSwitchSocket({ data }));
        dispatch(handleEssSSRStateSocket(data));
      });

      newSocket.on(SOCKET_EVENTS.BLOWER_AUDIT, (data) => {
        if (data.eventDeviceType === DEVICE_TYPES.TGS) {
          dispatch(handleTgsSwitchSocket({ data }));
        } else if (data.eventDeviceType === DEVICE_TYPES.TES) {
          dispatch(handleTesSwitchSocket({ data }));
          dispatch(handleTesSSRStateSocket(data));
        }
      });

      newSocket.on(SOCKET_EVENTS.SSR_UPDATE, (data) => {
        const specs = buildSpecsFromHeaters(data.Heaters, elementsOptionsRef.current);
        const newData = {
          ...data,
          specs: specs.length !== 0 ? specs : [{}],
        }
        if (data.eventDeviceType === DEVICE_TYPES.ESS) {
          dispatch(handleEssSSRStateSocket(newData));
        } else if (data.eventDeviceType === DEVICE_TYPES.TES) {
          dispatch(handleTesSSRStateSocket(newData));
        }
      });

      newSocket.on(SOCKET_EVENTS.SCHEDULE_LIST, (data) => {
        const location = data.zoneInfo.zone_id;
        const machine = data.device_mac;
        const clearData = [
          {
            start: { date: null, time: null },
            end: { date: null, time: null },
            inputTemp: null,
            id: null,
          },
        ];

        const addSchedule = data.eventDeviceType === DEVICE_TYPES.ESS
          ? handleAddHeatingSchedule
          : data.eventDeviceType === DEVICE_TYPES.TES
            ? tesHandleAddHeatingSchedule
            : tgsHandleAddHeatingSchedule;

        const clearSchedule = data.eventDeviceType === DEVICE_TYPES.ESS
          ? handleClearHeatingSchedule
          : data.eventDeviceType === DEVICE_TYPES.TES
            ? tesHandleClearHeatingSchedule
            : tgsHandleClearHeatingSchedule;

        if (data.hasOwnProperty('threshold')) {
          const { threshold, startDate, endDate, id } = data;
          dispatch(addSchedule({
            location,
            machine,
            start: readableTime(startDate),
            end: readableTime(endDate),
            index: 0,
            inputTemp: +threshold,
            id,
          }));
        } else {
          dispatch(clearSchedule({ location, machine, data: clearData }));
        }
      });

      newSocket.on(SOCKET_EVENTS.THERMOCOUPLE_UPDATE, (data) => {
        const swtName = data.eventDeviceType.toLowerCase();
        const newData = {
          swtName,
          location: data.zoneInfo?.zone_id,
          machine: data.device_id,
          data: { ...data, deviceType: swtName },
        }
        dispatch(handleReceivedThermocoupleSetting(newData));
      });

      const handleGraph = (data, graphType) => {
        const newData = {
          location: data.zoneInfo?.zone_id,
          machine: data.device_mac,
          graphType: getGraphTypeKey(graphType),
          data: data.points,
        };
        if (data.eventDeviceType === DEVICE_TYPES.ESS) {
          dispatch(handleEssGraph(newData));
        } else if (data.eventDeviceType === DEVICE_TYPES.TGS) {
          dispatch(handleTgsGraph(newData));
        } else if (data.eventDeviceType === DEVICE_TYPES.TES) {
          dispatch(handleTesGraph(newData));
        }
      };

      Object.values(GRAPH_TYPES).forEach((graphType) => {
        newSocket.on(graphType, (data) => handleGraph(data, graphType));
      });

      newSocket.on('disconnect', () => {
        logger.info('WebSocket connection closed.');
        setConnected(false);
        missedUpdates = true;
      });

      setSocket(newSocket);

      return () => {
        newSocket.off();
        newSocket.disconnect();
        setConnected(true);
      };
    }
  }, [room, accessToken, dispatch]);

  const sendMessage = (message) => {
    if (socket && socket.connected) {
      socket.send(message);
    } else {
      logger.debug('WebSocket connection is not open yet.');
    }
  };

  return { sendMessage, connected };
};
